// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, StationDetail } from '../types';
import { loadStationDetail, ols } from '../data';
import { anomalyColor } from '../color';
import { anomalyLegend } from '../color';
import { fmtRate, esc, MONTHS, attr } from '../format';
import { gloss } from '../glossary';
import { sectionHead, legendHtml, statTile } from './ui';

const SEASONS: Array<{ key: string; name: string; months: number[]; color: string }> = [
  { key: 'DJF', name: 'Summer (Dec–Feb)', months: [11, 0, 1], color: '#d9614a' },
  { key: 'MAM', name: 'Autumn (Mar–May)', months: [2, 3, 4], color: '#e0a24a' },
  { key: 'JJA', name: 'Winter (Jun–Aug)', months: [5, 6, 7], color: '#3b6fb0' },
  { key: 'SON', name: 'Spring (Sep–Nov)', months: [8, 9, 10], color: '#5aa469' },
];

export function mount(el: HTMLElement, data: ClimateData): void {
  const opts = data.stations.filter((s) => s.hasBaseline).slice().sort((a, b) => a.name.localeCompare(b.name));
  const def = opts.find((s) => s.id === '086338') || opts[0];
  let sel = localStorage.getItem('warm.seasons.sel') || def.id;
  if (!opts.some((s) => s.id === sel)) sel = def.id;

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('When in the year did it warm?', `A grid of every month of every year for one ${gloss('station', 'station')}, coloured by its ${gloss('anomaly', 'anomaly')}. Read top-to-bottom: the grid reddens as the decades pass. Some places warm most in summer, others in winter.`)}
      <div class="controls">
        <label>Station
          <select data-sel>${opts.map((s) => `<option value="${s.id}" ${s.id === sel ? 'selected' : ''}>${esc(s.name)} (${s.state})</option>`).join('')}</select>
        </label>
      </div>
      <div data-season-tiles class="tiles"></div>
      <div class="seasons-grid">
        <div class="heat-col">
          <h3 class="sub-h">Month × year anomaly</h3>
          <div class="heat-big" data-heat>Loading…</div>
          ${legendHtml('Cooler', 'Warmer', anomalyLegend(2))}
        </div>
        <div class="season-col">
          <h3 class="sub-h">Warming by season (°C/decade)</h3>
          <div data-season-bars></div>
          <p class="muted note">Seasonal rates are the trend of each season's mean anomaly over the station's record.</p>
        </div>
      </div>
    </section>`;

  const render = async () => {
    const heatEl = el.querySelector('[data-heat]') as HTMLElement;
    heatEl.textContent = 'Loading…';
    let detail: StationDetail;
    try { detail = await loadStationDetail(sel); }
    catch { heatEl.innerHTML = '<p class="muted">Could not load station detail.</p>'; return; }
    heatEl.innerHTML = bigHeat(detail);
    // seasonal trends
    const rows = detail.ym;
    const bars = SEASONS.map((se) => {
      const series: Array<[number, number]> = [];
      for (const [year, months] of rows) {
        const vals = se.months.map((mi) => months[mi]).filter((v) => v != null) as number[];
        if (vals.length === se.months.length) series.push([year, vals.reduce((s, x) => s + x, 0) / vals.length]);
      }
      const fit = ols(series);
      return { ...se, rate: fit ? fit.slope * 10 : null };
    });
    const maxRate = Math.max(0.01, ...bars.map((b) => (b.rate ?? 0)));
    (el.querySelector('[data-season-bars]') as HTMLElement).innerHTML = bars.map((b) => `
      <div class="sbar-row">
        <span class="sbar-name">${b.name}</span>
        <span class="sbar-track"><span class="sbar-fill" style="width:${Math.max(2, ((b.rate ?? 0) / maxRate) * 100)}%;background:${b.color}" data-tip="${attr(b.name + ': ' + fmtRate(b.rate))}"></span></span>
        <span class="sbar-val">${fmtRate(b.rate)}</span>
      </div>`).join('');
    const warmest = bars.filter((b) => b.rate != null).sort((a, b) => (b.rate as number) - (a.rate as number))[0];
    const st = data.stations.find((s) => s.id === sel)!;
    (el.querySelector('[data-season-tiles]') as HTMLElement).innerHTML =
      statTile(fmtRate(st.rateMean), 'Overall warming', `${st.state} · ${st.startYear}–${st.endYear}`) +
      statTile(warmest ? warmest.name.split(' ')[0] : '—', 'Fastest-warming season', warmest ? fmtRate(warmest.rate) : '') +
      statTile(fmtRate(st.rateMin), 'Nights', `days ${fmtRate(st.rateMax)}`);
  };

  el.querySelector('[data-sel]')?.addEventListener('change', (e) => {
    sel = (e.target as HTMLSelectElement).value; localStorage.setItem('warm.seasons.sel', sel); render();
  });
  render();
}

function bigHeat(detail: StationDetail): string {
  const rows = detail.ym.filter((r) => r[1].some((v) => v != null));
  if (!rows.length) return '<p class="muted">No monthly data.</p>';
  const years = rows.map((r) => r[0]);
  const w = 720, top = 18, left = 34;
  const cellH = Math.max(3, Math.min(9, 620 / rows.length));
  const cellW = (w - left) / 12;
  let g = '';
  for (let mi = 0; mi < 12; mi++) g += `<text x="${(left + mi * cellW + cellW / 2).toFixed(1)}" y="12" class="heat-lbl" text-anchor="middle">${MONTHS[mi]}</text>`;
  rows.forEach((r, ri) => {
    const y = top + ri * cellH;
    r[1].forEach((v, mi) => {
      g += `<rect x="${(left + mi * cellW).toFixed(1)}" y="${y.toFixed(1)}" width="${(cellW + 0.4).toFixed(1)}" height="${(cellH + 0.4).toFixed(1)}" fill="${anomalyColor(v, 2)}" data-tip="${r[0]} ${MONTHS[mi]}: ${v == null ? 'no data' : (v > 0 ? '+' : '') + v.toFixed(1) + ' °C'}"/>`;
    });
  });
  [years[0], years[Math.floor(years.length / 2)], years[years.length - 1]].forEach((yr) => {
    const ri = years.indexOf(yr);
    if (ri >= 0) g += `<text x="2" y="${(top + ri * cellH + cellH).toFixed(1)}" class="heat-lbl">${yr}</text>`;
  });
  const h = top + rows.length * cellH + 6;
  return `<svg viewBox="0 0 ${w} ${h}" class="heat-svg big" role="img" aria-label="Month by year anomaly heatmap">${g}</svg>`;
}
